export async function runQuickPricePolly(body, signal) {
    const res = await fetch(`${process.env.REACT_APP_TPOC_API_DOMAIN}/api/pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
    });
    if (!res.ok) throw new Error(`Pricing request failed (HTTP ${res.status})`);

    const text = await res.text();
    let parsed;
    try {
        parsed = JSON.parse(text);
    } catch {
        throw new Error('Pricing engine returned an unexpected response.');
    }
    // The engine sometimes double-encodes its payload.
    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
}

const sortPricesByRateAsc = (program) => ({
    ...program,
    prices: Array.isArray(program?.prices)
        ? [...program.prices].sort((a, b) => a.rate?.toFixed(3) - b.rate?.toFixed(3))
        : []
});

export function interpretPollyResponse(scenario, response, isCorrespondent = false) {
    console.log('Polly Response: ', response);

    /*
    if (response.RatesNotAvailable) {
      return { status: 'unavailable', message: 'Rates not available' };
    }
    */

    const { productNamePrefix } = scenario;
    const results = response.data.results ?? [];
    const hasPrices = p => p.prices?.length > 0;
    let eligibles = results.filter(p => p.isEligible && hasPrices(p)) ?? [];
    let ineligibles = results.filter(p => (!p.isEligible || !hasPrices(p)) && p.code.startsWith(productNamePrefix)) ?? [];
    // Remove duplicate discounts in prices.
    eligibles = eligibles.map(item => ({ ...item, prices: [...new Map(item.prices.map(item => [item.discount, item])).values()] }));

    if (!isCorrespondent) {
        eligibles = eligibles?.map(sortPricesByRateAsc);
        ineligibles = ineligibles?.map(sortPricesByRateAsc);
    }

    /*
    if (!response.Successed) {
      return {
        status: 'error',
        title: response.ResponseCode === 3 ? 'No matching programs found' : 'An error occurred when pricing',
        message: response.Message ?? '',
      };
    }
    */

    if (eligibles.length > 0) {
        const lockDeskWorkflowResults = eligibles[0]?.lockDeskWorkflowResults ?? [];
        const isLockEligible = lockDeskWorkflowResults.find(f => f.name.toLowerCase() === "LOCK".toLowerCase())?.isEnabled;
        return {
            status: 'priced',
            programs: eligibles,
            limits: results.Response?.Limits ?? null,
            loanType: eligibles[0]?.loanType,
            // pricingTimeStamp: convertDateTimeToUserFormat(isLockEligible ? response.data?.pricingGeneratedAt : ""),
            pricingTimeStamp: convertDateTimeToUserFormat(response.data?.configurationAsOf),
            rateExpired: !isLockEligible,
            secondIneligibles: results.Response?.SecondIneligibles ?? [],
        };
    }

    if (ineligibles.length > 0) {
        return { status: 'ineligible', programs: ineligibles };
    }

    return {
        status: 'none',
        title: 'No eligible programs',
        message: results.Message || 'There are no eligible programs for your loan.',
    };
}

function convertDateTimeToUserFormat(dateTime = "") {
    let currentTimestamp = new Date();

    if (dateTime !== "") {
        currentTimestamp = new Date(dateTime);
    }

    const formattedDate = currentTimestamp.toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        month: "numeric",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true
    }).replace(',', '');

    return formattedDate;
}

/**
 * Picks the rate option nearest par, as getDefaultOptinIndex() does.
 *
 * The two channels quote on different scales: Wholesale returns discount points
 * around 0, correspondent returns a price around 100 — so "par" differs.
 */
export function defaultOptionIndex(items = [], correspondent = false) {
    if (items.length === 0) return 0;
    return correspondent ? correspondentDefault(items) : wholesaleDefault(items);
}

/** Wholesale quotes discount points, so par is 0 and the nearest option wins. */
function wholesaleDefault(items) {
    let best = 0;
    let bestDistance = Math.abs(items[0].discount ?? 0);
    items.forEach((item, i) => {
        const distance = Math.abs(item.discount ?? 0);
        if (distance <= bestDistance) {
            bestDistance = distance;
            best = i;
        }
    });
    return best;
}

/**
 * Correspondent quotes a price, so par is 100.
 *
 * This mirrors getDefaultOptinIndex() literally, quirks included: an option
 * priced above par can never win, because the running best starts at 100 and
 * the comparison for a sub-par price is against its negation. The practical
 * effect is "the first option at or below par", and if every option is above
 * par the first one is kept. Written out rather than tidied so the default rate
 * matches the original on every rate sheet, not just today's.
 */
function correspondentDefault(items) {
    let index = 0;
    let best = 100;
    for (let i = 0; i < items.length; i += 1) {
        const price = items[i].price ?? 0;
        if (price === 100) return i;
        if (price < 100 && -price <= best) {
            best = -price;
            index = i;
        }
    }
    return index;
}
